import { AppleOutlined, CloudUploadOutlined, WindowsOutlined } from '@ant-design/icons';
import { Alert, App, Button, DatePicker, Form, Input, Progress, Tag, Upload } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel } from '../components';
import { formatBytes, formatDate, useLoad } from '../lib';

const platformInfo = {
  windows: { label: 'Windows', icon: <WindowsOutlined />, accept: '.exe,.msi', hint: 'EXE or MSI installer' },
  macos: { label: 'macOS', icon: <AppleOutlined />, accept: '.dmg,.pkg,.zip', hint: 'DMG, PKG or signed ZIP' }
};

export function DesktopReleasesPage() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [files, setFiles] = useState({ windows: null, macos: null });
  const [uploading, setUploading] = useState('');
  const [progress, setProgress] = useState(0);
  const { data, error, loading, reload } = useLoad(() => api('/v1/admin/releases/desktop').then((body) => body.release));

  useEffect(() => {
    if (data) form.setFieldsValue({ version: data.version, title: data.title, releasedAt: data.releasedAt ? dayjs(data.releasedAt) : dayjs() });
  }, [data, form]);

  async function publish(platform) {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    const file = files[platform];
    if (!file) return message.error(`Choose the ${platformInfo[platform].label} installer.`);
    setUploading(platform);
    setProgress(8);
    try {
      const details = { platform, version: values.version.trim(), title: (values.title || '').trim(), releasedAt: values.releasedAt.format('YYYY-MM-DD'), fileName: file.name, sizeBytes: file.size };
      const prepared = await api('/v1/admin/releases/desktop/upload', { method: 'POST', body: JSON.stringify(details) });
      setProgress(25);
      const uploaded = await fetch(prepared.uploadUrl, { method: 'PUT', body: file });
      if (!uploaded.ok) throw new Error(`B2 rejected the upload (${uploaded.status}). Check the bucket CORS rule for the ADMIN address.`);
      setProgress(80);
      await api('/v1/admin/releases/desktop/complete', { method: 'POST', body: JSON.stringify({ ...details, objectKey: prepared.objectKey }) });
      setProgress(100);
      message.success(`${platformInfo[platform].label} ${details.version} is live.`);
      setFiles((current) => ({ ...current, [platform]: null }));
      await reload();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'The installer could not be published.');
    } finally {
      setUploading('');
      setProgress(0);
    }
  }

  return <>
    <PageHeader title="Desktop releases" description="Publish Windows and macOS installers to the dedicated B2 release folder. The website automatically serves the current file for each operating system." />
    <ErrorNotice error={error} />
    {loading && !data ? <FormSkeleton sections={3} /> : <Form form={form} layout="vertical" requiredMark={false}>
      <Panel title="Release details" description="The version and release date are shared by both platform installers.">
        <div className="form-row three">
          <Form.Item label="Version" name="version" rules={[{ required: true, whitespace: true, message: 'Enter a version.' }]}><Input placeholder="1.4.0" className="mono" /></Form.Item>
          <Form.Item label="Release title" name="title"><Input placeholder="Faster bank import and macOS support" /></Form.Item>
          <Form.Item label="Release date" name="releasedAt" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} placeholder="Select release date" /></Form.Item>
        </div>
      </Panel>
      <div className="release-platform-grid">
        {Object.entries(platformInfo).map(([platform, info]) => {
          const current = data?.platforms?.[platform];
          const busy = uploading === platform;
          return <Panel key={platform} title={<span>{info.icon} {info.label}</span>} description={info.hint} extra={current ? <Tag color="success" variant="filled">Published</Tag> : <Tag variant="filled">Not published</Tag>}>
            {current && <div className="release-current"><strong>{current.fileName}</strong><span>{formatBytes(current.sizeBytes)} · uploaded {formatDate(current.uploadedAt)}</span></div>}
            <Upload.Dragger accept={info.accept} maxCount={1} fileList={files[platform] ? [files[platform]] : []} beforeUpload={(file) => { setFiles((value) => ({ ...value, [platform]: file })); return false; }} onRemove={() => setFiles((value) => ({ ...value, [platform]: null }))} disabled={Boolean(uploading)}>
              <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
              <p className="ant-upload-text">Choose or drop the {info.label} installer</p>
              <p className="ant-upload-hint">Stored under desktop-releases/&lt;version&gt;/{platform}/ in B2.</p>
            </Upload.Dragger>
            {busy && <Progress percent={progress} status="active" />}
            <Button type="primary" block icon={<CloudUploadOutlined />} loading={busy} disabled={Boolean(uploading) || !files[platform]} onClick={() => void publish(platform)}>Publish {info.label}</Button>
          </Panel>;
        })}
      </div>
      <Alert type="info" showIcon title="Safe rollout" description="Publishing one platform does not remove the other. Existing installer objects remain in B2 so they can be retained or removed according to your storage policy." />
    </Form>}
  </>;
}
